<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            `${isAdd ? $t("actions.add") : $t("actions.edit")} ${$tc(
              "types_alarms.title",
              1
            )}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- description -->
          <validation-provider
            #default="validationContext"
            name="description"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.description')"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                data-cy="description"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- description -->
          <validation-provider
            #default="validationContext"
            name="acronym"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.acronym')"
              label-for="acronym"
            >
              <b-form-input
                id="acronym"
                v-model="itemData.acronym"
                :state="getValidationState(validationContext)"
                data-cy="cronym"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" name="color">
            <b-form-group
              class="text-capitalize"
              :label="$t('alarm_level.color')"
              label-for="color"
            >
              <b-form-input
                id="color"
                v-model="itemData.color"
                :state="getValidationState(validationContext)"
                trim
                type="color"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" name="text_color">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.text_color')"
              label-for="text_color"
            >
              <b-form-input
                id="text_color"
                v-model="itemData.text_color"
                :state="getValidationState(validationContext)"
                trim
                type="color"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" name="color_icon">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.color_icon')"
              label-for="color_icon"
            >
              <b-form-input
                id="color_icon"
                v-model="itemData.color_icon"
                :state="getValidationState(validationContext)"
                trim
                type="color"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Icon -->
          <validation-provider #default="validationContext" name="icon">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.icon')"
              label-for="icon"
            >
              <!-- Icon Selector Button -->
              <div
                class="icon-selector-btn border rounded p-1 d-flex align-items-center justify-content-between cursor-pointer"
                @click="showIconPicker = !showIconPicker"
              >
                <div class="d-flex align-items-center">
                  <custom-icon
                    v-if="itemData.icon"
                    :name="itemData.icon"
                    size="28"
                    :color="itemData.color_icon"
                  />
                  <feather-icon v-else icon="ImageIcon" size="28" class="text-muted" />
                  <span class="ml-2">{{
                    itemData.icon || $t("types_alarms.no_icon")
                  }}</span>
                </div>
                <!-- Clear Icon Button -->
                <div>
                  <b-button
                    v-if="itemData.icon"
                    variant="outline-danger"
                    size="sm"
                    class="mr-1"
                    @click="clearIcon"
                  >
                    <feather-icon icon="XIcon" size="14" class="" />
                  </b-button>
                  <feather-icon
                    :icon="showIconPicker ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="18"
                  />
                </div>
              </div>

              <!-- Icon Picker Dropdown -->
              <div v-if="showIconPicker" class="icon-picker-dropdown mt-2">
                <!-- Search filter -->
                <b-form-input
                  v-model="iconSearch"
                  :placeholder="$t('table.search') + '...'"
                  class="mb-2"
                  trim
                />

                <!-- Icon Grid -->
                <div class="icon-grid-container border rounded p-2">
                  <div class="icon-grid">
                    <div
                      v-for="icon in filteredIcons"
                      :key="icon"
                      class="icon-grid-item"
                      :class="{ selected: itemData.icon === icon }"
                      @click="selectIcon(icon)"
                      :title="icon"
                    >
                      <custom-icon :name="icon" size="24" :color="itemData.color_icon" />
                    </div>
                  </div>
                  <div
                    v-if="filteredIcons.length === 0"
                    class="text-center text-muted py-2"
                  >
                    {{ $t("no_record_found") }}
                  </div>
                </div>
              </div>

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- alarm priority -->
          <validation-provider #default="validationContext" name="priority">
            <b-form-checkbox
              id="priority"
              v-model="itemData.priority"
              name="priority"
              value="1"
              unchecked-value="0"
              class="my-1 text-capitalize"
              switch
            >
              {{ $t("types_alarms.priority") }}
            </b-form-checkbox>
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>

          <template v-if="isPriorityOn">
            <validation-provider
              #default="validationContext"
              name="priority_comment"
              :rules="priorityCommentRules"
            >
              <b-form-group
                class="text-capitalize"
                :label="$t('types_alarms.priority_properties.description')"
                label-for="priority_comment"
              >
                <b-form-textarea
                  id="priority_comment"
                  v-model="itemData.priority_comment"
                  :state="getValidationState(validationContext)"
                  data-cy="priority_comment"
                  rows="3"
                  trim
                  placeholder="..."
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.priority_properties.time_label')"
              label-for="priority_duration"
              :state="durationError ? false : null"
            >
              <duration-hm-input
                :label="''"
                :value="priorityDurationValue"
                :max-hours="999"
                :state="durationError ? false : null"
                data-cy="priority_duration"
                @input="onPriorityDurationInput"
              />
              <small v-if="durationError" class="text-danger d-block mt-50">{{ durationError }}</small>
            </b-form-group>
          </template>

          <b-form-checkbox
            id="automatic-reset-enabled"
            v-model="automaticResetEnabled"
            class="my-1"
            switch
            data-cy="automatic-reset-enabled"
          >
            {{ $t("types_alarms.automatic_reset") }}
          </b-form-checkbox>
          <b-form-group
            v-if="automaticResetEnabled"
            :label="$t('types_alarms.automatic_reset_minutes')"
            label-for="automatic-reset-minutes"
          >
            <b-form-input
              id="automatic-reset-minutes"
              v-model="itemData.automatic_reset_minutes"
              type="number"
              min="1"
              max="2147483647"
              step="1"
              required
              :state="automaticResetError ? false : null"
              data-cy="automatic-reset-minutes"
              @input="automaticResetError = null"
            />
            <b-form-invalid-feedback :state="automaticResetError ? false : null">
              {{ automaticResetError }}
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- Allow Multiple -->
          <validation-provider #default="validationContext" name="allow_multiple">
            <b-form-checkbox
              id="allow_multiple"
              v-model="itemData.allow_multiple"
              name="allow_multiple"
              :value="true"
              :unchecked-value="false"
              class="my-1 text-capitalize"
              switch
            >
              {{ $t("types_alarms.allow_multiple") }}
            </b-form-checkbox>
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
              data-cy="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed, getCurrentInstance } from "@vue/composition-api";
import { required, alphaNum, positive } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { ICON_NAMES } from "@/assets/svg/CustomIcons";
import DurationHmInput from "@/components/BuildRiskForm/DurationHmInput.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    DurationHmInput,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      positive,
      durationError: null,
    };
  },
  setup(props, { emit }) {
    const automaticResetEnabled = ref(false);
    const automaticResetError = ref(null);
    const blankData = {
      automatic_reset_minutes: null,
      description: "",
      acronym: "",
      color: "",
      text_color: "",
      icon: "",
      allow_multiple: false,
      priority: "0",
      priority_comment: "",
      priority_hour: "",
      priority_minutes: "",
      color_icon: ""
    };

    const iconOptions = ICON_NAMES;
    const iconSearch = ref("");
    const showIconPicker = ref(false);

    const filteredIcons = computed(() => {
      if (!iconSearch.value) return iconOptions;
      const search = iconSearch.value.toLowerCase();
      return iconOptions.filter((name) => name.toLowerCase().includes(search));
    });

    const isPriorityOn = computed(() => {
      const p = itemData.value.priority;
      return p === "1" || p === 1;
    });

    const priorityCommentRules = computed(() =>
      isPriorityOn.value ? "required" : ""
    );

    const priorityDurationValue = computed(() => ({
      hours: Number(itemData.value.priority_hour) || 0,
      minutes: Number(itemData.value.priority_minutes) || 0,
    }));

    const instance = getCurrentInstance();

    const onPriorityDurationInput = (value) => {
      itemData.value.priority_hour = value.hours;
      itemData.value.priority_minutes = value.minutes;
      if (instance?.proxy) instance.proxy.durationError = null;
    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      automaticResetEnabled.value = false;
      automaticResetError.value = null;
      itemData.value = JSON.parse(JSON.stringify(blankData));
      if (instance?.proxy) instance.proxy.durationError = null;
    };

    const normalizePriority = (value) => {
      if (value === "1" || value === 1 || value === true) return "1";
      return "0";
    };

    const buildPayload = (data) => ({
      automatic_reset_minutes: automaticResetEnabled.value ? Number(data.automatic_reset_minutes) : null,
      description: data.description ?? "",
      acronym: data.acronym ?? "",
      color: data.color ?? "",
      text_color: data.text_color ?? "",
      icon: data.icon ?? "",
      color_icon: data.color_icon ?? "",
      allow_multiple: data.allow_multiple ?? false,
      priority: normalizePriority(data.priority),
      priority_comment: data.priority_comment ?? "",
      priority_hour: data.priority_hour !== undefined && data.priority_hour !== "" ? Number(data.priority_hour) : 0,
      priority_minutes: data.priority_minutes !== undefined && data.priority_minutes !== "" ? Number(data.priority_minutes) : 0,
    });



    const onSubmit = () => {
      if (automaticResetEnabled.value) {
        const minutes = Number(itemData.value.automatic_reset_minutes);
        if (!Number.isInteger(minutes) || minutes < 1 || minutes > 2147483647) {
          automaticResetError.value = instance.proxy.$t("types_alarms.automatic_reset_invalid");
          return;
        }
      }
      automaticResetError.value = null;
      if (isPriorityOn.value) {
        const h = Number(itemData.value.priority_hour) || 0;
        const m = Number(itemData.value.priority_minutes) || 0;
        if (h === 0 && m === 0) {
          if (instance?.proxy) {
            instance.proxy.durationError = instance.proxy.$t("types_alarms.priority_properties.time_required_message");
          }
          return;
        }
      }
      if (instance?.proxy) instance.proxy.durationError = null;
      const req = buildPayload(itemData.value);

      let dispatch;

      // If priority is off, set all related fields to null before dispatching
      if (req.priority === "0") {
        req.priority_comment = null;
        req.priority_hour = null;
        req.priority_minutes = null;
      }
      if (props.isAdd) {
        dispatch = store.dispatch("type_alarm/add", req);
      } else {
        const data = {
          id: itemData.value.id,
          params: req,
        };

        dispatch = store.dispatch("type_alarm/edit", data);
      }

      dispatch
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          emit("refetch-data");
          emit("update:is-sidebar-active", false);
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const loadData = () => {
      if (props.isAdd) {
        resetData();
        return;
      }
      const merged = { ...blankData, ...props.itemEdit };
      merged.priority = normalizePriority(merged.priority);
      merged.priority_comment = merged.priority_comment ?? "";
      merged.priority_hour = merged.priority_hour !== undefined && merged.priority_hour !== null ? merged.priority_hour : "";
      merged.priority_minutes = merged.priority_minutes !== undefined && merged.priority_minutes !== null ? merged.priority_minutes : "";
      merged.automatic_reset_minutes = merged.automatic_reset_minutes ?? null;
      automaticResetEnabled.value = merged.automatic_reset_minutes !== null;
      automaticResetError.value = null;
      itemData.value = merged;
    };

    const selectIcon = (icon) => {
      itemData.value.icon = icon;
      showIconPicker.value = false;
      iconSearch.value = "";
    };

    const clearIcon = () => {
      itemData.value.icon = "";
      showIconPicker.value = false;
      iconSearch.value = "";
    };

    return {
      itemData,
      automaticResetEnabled,
      automaticResetError,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      iconOptions,
      iconSearch,
      filteredIcons,
      showIconPicker,
      selectIcon,
      clearIcon,
      isPriorityOn,
      priorityCommentRules,
      priorityDurationValue,
      onPriorityDurationInput,
    };
  },
};
</script>

<style scoped>
.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: 8px;
}

.icon-grid:before {
  content: unset;
}

.icon-grid-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.icon-grid-item.selected img {
  filter: brightness(0) invert(1);
}

.icon-grid-container::-webkit-scrollbar {
  width: 6px;
}

.icon-grid-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb:hover {
  background-color: #999;
}

.icon-selector-btn {
  transition: all 0.2s ease;
}

.icon-grid-container {
  max-height: 250px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

</style>
